/* Curated example snippets — code only; canonical outputs come from the real
 * runtime (build-time snapshot in default-output-snapshot.ts). */

export interface Snippet {
  id: string;
  emoji: string;
  title: string;
  desc: string;
  code: string;
  /** optional STDIN to preload when the snippet is picked. */
  stdin?: string;
}

export const DEFAULT_CODE = `<?php

$name = "php.onl";
echo "Hello from {$name}! 🐘\\n";
echo "Running PHP " . PHP_VERSION . PHP_EOL;
printf("Current year: %d.\\n", (int) date("Y"));
`;

export const SNIPPETS: Snippet[] = [
  {
    id: 'hello',
    emoji: '👋',
    title: 'Hello, world',
    desc: 'Basics: echo + version',
    code: DEFAULT_CODE,
  },
  {
    id: 'match',
    emoji: '🎯',
    title: 'match() expression',
    desc: 'Compare 7.4 vs 8.x',
    code: `<?php
// match() was added in PHP 8.0
$code = 404;

$label = match($code) {
    200, 201 => "OK",
    301, 302 => "Redirect",
    404       => "Not Found",
    default   => "Unknown",
};

echo "HTTP {$code} → {$label}\\n";
`,
  },
  {
    id: 'enum',
    emoji: '🏷️',
    title: 'Enums',
    desc: 'Requires PHP 8.1+',
    code: `<?php
// enum available since PHP 8.1
enum Suit: string {
    case Hearts   = '♥';
    case Diamonds = '♦';
    case Clubs    = '♣';
    case Spades   = '♠';
}

foreach (Suit::cases() as $s) {
    echo $s->name . " = " . $s->value . "\\n";
}
`,
  },
  {
    id: 'strcontains',
    emoji: '🔎',
    title: 'str_contains()',
    desc: 'New functions in 8.0',
    code: `<?php
$haystack = "The quick brown fox";

// str_contains() was added in PHP 8.0
if (str_contains($haystack, "brown")) {
    echo "Found 'brown'!\\n";
}

var_dump(str_contains($haystack, "cat"));
`,
  },
  {
    id: 'deprecate',
    emoji: '⚠️',
    title: 'Dynamic properties',
    desc: 'Deprecation in 8.2+',
    code: `<?php
class Box {}

$b = new Box();
$b->color = "yellow";   // dynamic property assignment

echo "Box color: {$b->color}\\n";
`,
  },
  {
    id: 'array',
    emoji: '📦',
    title: 'Array & JSON',
    desc: 'print_r + json_encode',
    code: `<?php
$user = [
    "name"  => "Alice",
    "roles" => ["admin", "editor"],
    "active"=> true,
];

print_r($user);
echo json_encode($user, JSON_PRETTY_PRINT) . "\\n";
`,
  },
  {
    id: 'perf',
    emoji: '🏎️',
    title: 'Loop benchmark',
    desc: 'Compare speed across versions',
    code: `<?php
$start = microtime(true);

$sum = 0;
for ($i = 0; $i < 5_000_000; $i++) {
    $sum += $i;
}

$ms = (microtime(true) - $start) * 1000;
printf("Sum = %d\\n", $sum);
printf("Time: %.1f ms\\n", $ms);
`,
  },
  {
    id: 'stdin',
    emoji: '⌨️',
    title: 'Read STDIN',
    desc: 'Use the Input panel below',
    code: `<?php
// Type a name in the STDIN panel below, then Run
$line = trim(fgets(STDIN));
$name = $line !== "" ? $line : "guest";

echo "Welcome, {$name}!\\n";
echo "Name length: " . strlen($name) . " chars\\n";
`,
    stdin: 'Alice',
  },
];

const norm = (s: string) => s.replace(/\r/g, '').replace(/\s+$/gm, '').trim();

/** Equivalent ignoring trailing whitespace. */
export function sameCode(a: string, b: string): boolean {
  return norm(a) === norm(b);
}

/** Returns the matching snippet, or null. */
export function matchSnippet(code: string): Snippet | null {
  return SNIPPETS.find((s) => sameCode(s.code, code)) ?? null;
}
